import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';

import { logout as actionLogout } from '../actions/auth';
import actionChangeLang from '../actions/i18n';
import SubscribitionReminder from './SubscribitionReminder';

import './RtsUserNav.scss';

const LANGS = ['en', 'ru'];

const isEditorPath = pathname =>
  pathname.startsWith('/i/gui/snap') || pathname.startsWith('/i/gui/code');

const ADMIN_LINKS = [
  {
    to: '/i/gui/homework',
    titleKey: 'admin.navigation.homework',
    groups: ['admin', 'teacher'],
  },
  {
    to: '/i/gui/admin',
    titleKey: 'admin.navigation.courseEditor',
    groups: ['admin', 'author'],
  },
  {
    to: '/i/gui/workinghours',
    titleKey: 'admin.navigation.workingHours',
    groups: ['admin', 'manager', 'mentor'],
  },
  {
    to: '/i/gui/new-lessons',
    titleKey: 'admin.navigation.lessons',
    groups: ['admin', 'manager', 'mentor'],
  },
  {
    to: '/i/gui/users',
    titleKey: 'admin.navigation.users',
    groups: ['admin', 'manager', 'mentor'],
  },
  {
    to: '/i/gui/payments',
    titleKey: 'admin.navigation.payments',
    groups: ['admin', 'manager'],
  },
  {
    to: '/i/gui/promocodes',
    titleKey: 'admin.navigation.promocodes',
    groups: ['admin'],
  },
  {
    to: '/i/gui/subscriptions',
    titleKey: 'admin.navigation.subscriptions',
    groups: ['admin', 'manager'],
  },
  {
    to: '/i/gui/subs',
    titleKey: 'admin.navigation.userSubscriptions',
    groups: ['admin', 'manager'],
  },
  {
    to: '/i/gui/mails',
    titleKey: 'admin.navigation.mails',
    groups: ['admin', 'manager', 'mentor'],
  },
  {
    to: '/i/gui/teachers',
    title: 'Teachers',
    groups: ['admin', 'manager', 'mentor'],
  },
  {
    to: '/i/gui/playing',
    titleKey: 'admin.navigation.playing',
    groups: ['admin'],
  },
  {
    to: '/i/gui/links',
    title: 'links',
    groups: ['admin'],
  },
  {
    to: '/i/gui/external-api-logs',
    title: 'External API logs',
    groups: ['admin'],
  },
  {
    to: '/i/gui/chat',
    titleKey: 'admin.navigation.inGameChatLogs',
    groups: ['admin'],
  },
  {
    to: '/i/gui/attempts',
    titleKey: 'admin.navigation.attempts',
    groups: ['admin'],
  },
  {
    to: '/i/gui/invitations',
    titleKey: 'admin.navigation.invitations',
    groups: ['admin'],
  },
  {
    to: '/i/gui/mattermost',
    title: 'Mattermost',
    groups: ['admin', 'mentor'],
  },
  {
    to: '/i/gui/extrasalary',
    titleKey: 'admin.navigation.salaryExtra',
    groups: ['admin'],
  },
  {
    to: '/i/gui/salary',
    titleKey: 'admin.navigation.salary',
    groups: ['admin', 'manager', 'mentor'],
  },
  {
    to: '/i/gui/docker',
    title: 'Minecraft Servers',
    groups: ['admin', 'manager'],
  },
];

const GridIcon = () => (
  <span className="rts-header__grid-icon" aria-hidden="true">
    {Array.from({ length: 9 }).map((_, i) => (
      <span key={i} className="rts-header__grid-dot" />
    ))}
  </span>
);

const useCloseOnOutside = (isOpen, onClose) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClick = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose]);

  return ref;
};

const ProfileMenuItem = ({ to, onClick, icon, children, asButton }) => {
  const content = (
    <React.Fragment>
      <span className="rts-header__menu-item-icon" aria-hidden="true">
        <i className={`fa ${icon}`} />
      </span>
      <span className="rts-header__menu-item-label">{children}</span>
    </React.Fragment>
  );

  if (asButton) {
    return (
      <button
        type="button"
        className="rts-header__menu-item"
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <Link to={to} className="rts-header__menu-item" onClick={onClick}>
      {content}
    </Link>
  );
};

const RtsUserNav = ({
  pathname,
  user,
  locale,
  selectedCourse,
  logout,
  changeLang,
}) => {
  const [openMenu, setOpenMenu] = useState(null);
  const closeMenus = () => setOpenMenu(null);

  const adminRef = useCloseOnOutside(openMenu === 'admin', closeMenus);
  const profileRef = useCloseOnOutside(openMenu === 'profile', closeMenus);

  if (!user) {
    return (
      <header className="rts-header">
        <div className="rts-header__inner">
          <Link to="/i/all" className="rts-header__logo-link">
            <img
              className="rts-header__logo"
              src="/rts-logo.64cace62.png"
              alt="Rocket Tech School"
            />
          </Link>
          <div className="rts-header__actions">
            <button
              type="button"
              className="rts-header__text-btn"
              onClick={() => browserHistory.push('/i/auth')}
            >
              <Translate value="auth.login" />
            </button>
          </div>
        </div>
      </header>
    );
  }

  const isEmployee = ['admin', 'teacher', 'manager', 'mentor'].includes(
    user.group
  );
  const adminLinks = ADMIN_LINKS.filter(item => item.groups.includes(user.group));
  const showAdmin = adminLinks.length > 0;

  const toggleMenu = name => {
    setOpenMenu(current => (current === name ? null : name));
  };

  return (
    <header className="rts-header">
      <SubscribitionReminder user={user} location={pathname} />

      <div className="rts-header__inner">
        <div className="rts-header__start">
          <Link to="/i/all" className="rts-header__logo-link">
            <img
              className="rts-header__logo"
              src="/rts-logo.64cace62.png"
              alt="Rocket Tech School"
            />
            {user.school && (
              <img
                className="rts-header__logo rts-header__logo--school"
                src={require(`../assets/images/schools/${user.school}.png`)}
                alt={user.school}
              />
            )}
          </Link>

          {selectedCourse && isEditorPath(pathname) && (
            <Link
              to={`/i/${selectedCourse.type}/${selectedCourse._id}`}
              className="rts-header__lesson-back"
            >
              <i className="fa fa-angle-left" aria-hidden="true" />
              <span>
                <Translate value="nav.lessonback" />
              </span>
            </Link>
          )}
        </div>

        <div className="rts-header__actions">
        {user.userType !== 'child' && !user.confirmed && (
          <button
            type="button"
            className="rts-header__text-btn rts-header__text-btn--warning"
            onClick={() => browserHistory.push('/i/confirm')}
          >
            <Translate value="nav.confirm" />
          </button>
        )}

        {showAdmin && (
          <div className="rts-header__menu-wrap" ref={adminRef}>
            <button
              type="button"
              className={`rts-header__icon-btn${
                openMenu === 'admin' ? ' rts-header__icon-btn--active' : ''
              }`}
              aria-label={I18n.t('admin.navigation.title')}
              aria-expanded={openMenu === 'admin'}
              onClick={() => toggleMenu('admin')}
            >
              <GridIcon />
            </button>
            {openMenu === 'admin' && (
              <div className="rts-header__dropdown">
                <div className="rts-header__dropdown-title">
                  <Translate value="admin.navigation.title" />
                </div>
                {adminLinks.map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="rts-header__dropdown-link"
                    onClick={closeMenus}
                  >
                    {item.titleKey ? I18n.t(item.titleKey) : item.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="rts-header__menu-wrap" ref={profileRef}>
          <button
            type="button"
            className={`rts-header__icon-btn${
              openMenu === 'profile' ? ' rts-header__icon-btn--active' : ''
            }`}
            aria-label={I18n.t('nav.settings')}
            aria-expanded={openMenu === 'profile'}
            onClick={() => toggleMenu('profile')}
          >
            <i className="fa fa-user" aria-hidden="true" />
          </button>
          {openMenu === 'profile' && (
            <div className="rts-header__dropdown rts-header__dropdown--profile">
              {isEmployee && (
                <ProfileMenuItem
                  to="/i/profile"
                  icon="fa-user"
                  onClick={closeMenus}
                >
                  <Translate value="nav.profile" />
                </ProfileMenuItem>
              )}

              <ProfileMenuItem
                to="/i/settings"
                icon="fa-cog"
                onClick={closeMenus}
              >
                <Translate value="nav.settings" />
              </ProfileMenuItem>

              {isEmployee && (
                <React.Fragment>
                  <div className="rts-header__dropdown-divider" />

                  <div className="rts-header__dropdown-section rts-header__dropdown-section--lang">
                    <span className="rts-header__dropdown-label">
                      Язык / Language
                    </span>
                    <div className="rts-header__lang-btns">
                      {LANGS.map(lang => (
                        <button
                          key={lang}
                          type="button"
                          className={`rts-header__lang-btn${
                            locale === lang ? ' rts-header__lang-btn--active' : ''
                          }`}
                          onClick={() => {
                            changeLang(lang)();
                          }}
                        >
                          {lang.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                </React.Fragment>
              )}

              <div className="rts-header__dropdown-divider" />

              <ProfileMenuItem
                asButton
                icon="fa-sign-out"
                onClick={() => {
                  logout();
                  closeMenus();
                }}
              >
                <Translate value="auth.logout" />
              </ProfileMenuItem>
            </div>
          )}
        </div>
      </div>
      </div>
    </header>
  );
};

RtsUserNav.propTypes = {
  pathname: PropTypes.string.isRequired,
  user: PropTypes.any,
  locale: PropTypes.string.isRequired,
  selectedCourse: PropTypes.any,
  changeLang: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  user: state.auth.user,
  locale: state.i18n.locale,
  selectedCourse: state.courses.selectedCourse,
});

const mapDispatchToProps = dispatch => ({
  logout: () => dispatch(actionLogout()),
  changeLang: lang => () => dispatch(actionChangeLang(lang)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RtsUserNav);
